// Terminbuchung auf der Coming-Soon-Seite — ohne Fremddienst.
//
// Der Besucher waehlt Tag und Uhrzeit, traegt Name und E-Mail ein, fertig. Der
// gebuchte 15-Minuten-Slot ist danach fuer alle anderen weg.
//
// Kein Calendly und kein anderes eingebettetes Buchungswerkzeug: das haette
// Skripte und Cookies eines US-Anbieters auf eine Seite gebracht, die bewusst
// keinen Consent-Banner hat, und die Termine an einem zweiten Ort gefuehrt.
// Stattdessen laeuft alles ueber dieselbe Supabase-Instanz, mit der die Seite
// ohnehin spricht (EU-Region Irland; Kundendatenbanken in Frankfurt).
//
// Zwei Funktionen statt Tabellenzugriff — die Tabelle ist fuer anon zu:
//   forge_booking_slots()   liefert NUR freie Zeitpunkte, keine Personendaten.
//                           Belegt sieht von aussen aus wie „nicht angeboten".
//   forge_booking_create()  prueft Eingaben, Angebot und Doppelbelegung und
//                           antwortet mit {ok:false, error:…} statt zu werfen.
// Die eigentliche Sperre ist ein Unique-Index in der Datenbank; die Pruefung
// hier ist Komfort. Zwei Besucher, die im selben Moment klicken, koennen den
// Termin sonst beide zu bekommen glauben.
//
// SQL: deploy/sql/2026-08-20-terminbuchung.sql

import { bookingHTML, innerHTML, dayKey } from './booking-view.js';

export { bookingHTML };

// So weit im Voraus bietet die Seite Termine an. Mehr macht die Auswahl
// unuebersichtlich, weniger laesst Anfragen auflaufen, sobald eine Woche
// ausgebucht ist. Die Datenbank deckelt zusaetzlich bei 28 Tagen.
export const SLOT_DAYS = 14;

function rpc(cfg, fn, body) {
  return fetch(`${cfg.url}/rest/v1/rpc/${fn}`, {
    method: 'POST',
    headers: {
      apikey: cfg.key,
      Authorization: 'Bearer ' + cfg.key,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}

export async function fetchSlots(cfg, days = SLOT_DAYS) {
  const r = await rpc(cfg, 'forge_booking_slots', { p_days: days });
  if (!r.ok) throw new Error('slots ' + r.status);
  const rows = await r.json();
  // `returns table(slot timestamptz)` kommt als [{slot:'…'}] zurueck; ein
  // skalarer Rueckgabewert waere ein blankes Array. Beides annehmen.
  return (Array.isArray(rows) ? rows : []).map(x => (x && x.slot) || x).filter(Boolean);
}

// Die Sprache wird mitgebucht, nicht spaeter erraten: die Bestaetigungsmail
// verschickt ein Workflow Minuten spaeter und weiss von dieser Sitzung nichts
// mehr. Ohne sie bekaeme ein englischer Besucher eine deutsche Mail.
// Aus Themenwahl und Freitext wird eine Zeile, die in der Mail lesbar ist:
// gespeichert wird der Klartext des Themas, nicht seine Kennung — wer die
// Buchung spaeter liest, soll nicht in der Sprachtabelle nachschlagen muessen.
export function composeNote(tx, topic, note) {
  const text  = (note || '').trim();
  const found = (tx.cal_topics || []).find(t => t.v === topic);
  const label = found && found.l;
  if (label && text) return `${label} — ${text}`;
  return label || text;
}

export async function createBooking(cfg, { name, email, slot, lang, note, phone }) {
  const daten = {
    p_name: name, p_email: email, p_start: slot,
    // Leeres Feld heisst „nichts gesagt", nicht „leerer Text": die Datenbank
    // macht aus einem Leerstring ohnehin NULL, aber so steht es schon hier.
    p_note: (note || '').trim() || null,
    p_lang: lang === 'en' ? 'en' : 'de',
  };

  // p_phone nur mitschicken, wenn wirklich eine Nummer dasteht. Solange die
  // Datenbank die neue Signatur noch nicht hat, kennt PostgREST den Parameter
  // nicht und weist den ganzen Aufruf ab (PGRST202) — so scheitern in dieser
  // Luecke hoechstens Buchungen MIT Nummer, nicht jede.
  const tel = (phone || '').trim();
  if (tel) daten.p_phone = tel;

  const r = await rpc(cfg, 'forge_booking_create', daten);
  if (!r.ok) throw new Error('create ' + r.status);
  const out = await r.json();
  return out && typeof out === 'object' ? out : { ok: false, error: 'net' };
}

// Stoesst den Mail-Workflow an, damit die Bestaetigung sofort rausgeht statt
// beim naechsten Zeitplan-Lauf (bis zu 5 Minuten spaeter — wer gerade gebucht
// hat, haelt das fuer „es kommt nichts").
//
// Bewusst ohne Nutzlast: der Workflow verschickt, was in `bookings` mit
// notified_at IS NULL steht, nie was dieser Aufruf behauptet. Damit ist der
// offene Webhook harmlos — er kann nichts ausloesen, was nicht ohnehin faellig
// waere. Und bewusst fire-and-forget: die Buchung steht bereits in der
// Datenbank, der Zeitplan holt sie also auch dann nach, wenn dieser Aufruf
// scheitert oder der Besucher den Tab vorher schliesst.
export const MAIL_HOOK = 'https://schulz-solutions.app.n8n.cloud/webhook/forge-booking-created';

export function nudgeMailer(url = MAIL_HOOK) {
  try {
    fetch(url, { method: 'POST', mode: 'no-cors', keepalive: true })
      .catch(e => console.warn('Mail-Anstoss fehlgeschlagen:', e.message));
  } catch (e) {
    console.warn('Mail-Anstoss fehlgeschlagen:', e.message);
  }
}

// Verdrahtet den Block. Gehoert hinter das Rendern — vorher gibt es kein #cs-cal.
export function mountBooking(tx, cfg, lang = 'de') {
  const box = document.getElementById('cs-cal');
  if (!box) return null;

  const st = {
    phase: 'loading', slots: [], day: null, slot: null, busy: false, err: null,
    // Eingetipptes ueberlebt das Neuzeichnen — siehe booking-view.js.
    form: { name: '', email: '', note: '', topic: '', phone: '' },
  };
  const draw = () => { box.innerHTML = innerHTML(tx, st, lang); };

  async function load() {
    st.phase = 'loading';
    draw();
    try {
      st.slots = await fetchSlots(cfg);
      st.phase = 'pick';
      st.day = st.slots.length ? dayKey(st.slots[0]) : null;
    } catch (e) {
      console.warn('Termine nicht geladen:', e.message);
      st.phase = 'error';
    }
    draw();
  }

  async function book(name, email, note, topic, phone) {
    st.busy = true; st.err = null; draw();
    let out;
    try {
      out = await createBooking(cfg, {
        name, email, slot: st.slot, lang, note: composeNote(tx, topic, note), phone,
      });
    } catch (e) {
      console.warn('Termin nicht gebucht:', e.message);
      out = { ok: false, error: 'net' };
    }
    st.busy = false;

    if (out.ok) { st.phase = 'done'; draw(); nudgeMailer(); return; }

    st.err = out.error || 'net';
    // „Vergeben" und „nicht mehr im Angebot" heissen beide: die Liste ist alt.
    // Neu laden, damit der naechste Klick nicht denselben Fehler bringt — die
    // Meldung bleibt trotzdem stehen, sonst waere der Termin kommentarlos weg.
    if (st.err === 'taken' || st.err === 'slot') {
      const err = st.err;
      st.slot = null;
      await load();
      st.err = err;
      st.phase = 'pick';
    }
    draw();
  }

  // Vor jedem Neuzeichnen retten, was im Formular steht. Das Feld ist nach dem
  // naechsten innerHTML weg, der Wert im Zustand nicht.
  function merkeEingaben() {
    const feld = id => box.querySelector(id)?.value.trim();
    st.form.name  = feld('#cs-cal-name')  ?? st.form.name;
    st.form.email = feld('#cs-cal-mail')  ?? st.form.email;
    st.form.note  = feld('#cs-cal-msg')   ?? st.form.note;
    st.form.phone = feld('#cs-cal-tel')   ?? st.form.phone;
    // Die Auswahl wird nicht getrimmt — sie ist eine Kennung, kein Satz.
    st.form.topic = box.querySelector('#cs-cal-topic')?.value ?? st.form.topic;
  }

  box.addEventListener('click', e => {
    merkeEingaben();
    const day = e.target.closest('[data-day]');
    if (day) { st.day = day.dataset.day; st.slot = null; st.err = null; st.phase = 'pick'; return draw(); }

    const slot = e.target.closest('[data-slot]');
    if (slot) { st.slot = slot.dataset.slot; st.err = null; st.phase = 'form'; return draw(); }

    if (e.target.closest('[data-back]')) { st.slot = null; st.err = null; st.phase = 'pick'; return draw(); }
    if (e.target.closest('[data-retry]')) return load();
  });

  box.addEventListener('submit', e => {
    if (!e.target.closest('[data-form]')) return;
    e.preventDefault();
    if (st.busy || !st.slot) return;
    merkeEingaben();
    book(st.form.name, st.form.email, st.form.note, st.form.topic, st.form.phone);
  });

  load();
  return st;
}
